import { PASSWORD_MIN_LENGTH, type SessionUser } from '@edms/shared';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';

function AuthFrame({ children }: { children: ReactNode }) {
  return (
    <div className="auth">
      <section className="auth-art" aria-hidden>
        <span className="orb" style={{ width: 380, height: 380, right: -120, top: -100 }} />
        <span className="orb" style={{ width: 220, height: 220, left: -60, bottom: 40, animationDelay: '-6s' }} />
        <div className="logo" style={{ color: '#fff', position: 'relative' }}>
          <span className="logo-mark" />
          EDMS
        </div>
        <div>
          <h1>Документы идут сами</h1>
          <p>Задачи, командировки и отпуска проходят согласование по маршруту — вы видите, у кого документ и сколько он там лежит.</p>
          <div className="flow">
            <span>Черновик</span>
            <span>→ Согласование</span>
            <span>→ Документ готов</span>
          </div>
        </div>
      </section>
      <section className="auth-form">{children}</section>
    </div>
  );
}

function useSubmit() {
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError(0, 'Нет связи с сервером'));
    } finally {
      setBusy(false);
    }
  };
  return { error, busy, run };
}

export function LoginPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { error, busy, run } = useSubmit();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  if (user) return <Navigate to="/" replace />;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const me = await api.post<SessionUser>('/auth/login', { email, password });
      setUser(me);
      navigate((location.state as { from?: string } | null)?.from ?? '/', { replace: true });
    });
  };

  return (
    <AuthFrame>
      <form onSubmit={submit} noValidate>
        <div>
          <h1>Вход</h1>
          <p className="muted" style={{ marginTop: 6 }}>
            Рабочий email и пароль
          </p>
        </div>
        {error && (
          <div className="alert" role="alert">
            {error.message}
          </div>
        )}
        <label className="field">
          <span>Email</span>
          <input
            className="input"
            type="email"
            autoComplete="username"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={Boolean(error?.fields.email)}
          />
          {error?.fields.email && <span className="field-error">{error.fields.email}</span>}
        </label>
        <label className="field">
          <span>Пароль</span>
          <input
            className="input"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        <button className="btn btn-primary btn-block" disabled={busy}>
          {busy ? 'Входим…' : 'Войти'}
        </button>
        <Link to="/forgot-password" style={{ justifySelf: 'center' }}>
          Забыли пароль?
        </Link>
      </form>
    </AuthFrame>
  );
}

export function ForgotPasswordPage() {
  const { error, busy, run } = useSubmit();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
    });
  };

  return (
    <AuthFrame>
      <form onSubmit={submit} noValidate>
        <div>
          <h1>Восстановление пароля</h1>
          <p className="muted" style={{ marginTop: 6 }}>
            Пришлём ссылку для нового пароля на рабочую почту.
          </p>
        </div>
        {sent ? (
          <div className="alert ok" role="status">
            Если такой адрес есть в системе, письмо уже в пути. Ссылка действует 1 час.
          </div>
        ) : (
          <>
            {error && <div className="alert">{error.message}</div>}
            <label className="field">
              <span>Email</span>
              <input className="input" type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
              {error?.fields.email && <span className="field-error">{error.fields.email}</span>}
            </label>
            <button className="btn btn-primary btn-block" disabled={busy}>
              Отправить ссылку
            </button>
          </>
        )}
        <Link to="/login" style={{ justifySelf: 'center' }}>
          ← Ко входу
        </Link>
      </form>
    </AuthFrame>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { error, busy, run } = useSubmit();
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [mismatch, setMismatch] = useState(false);
  const token = params.get('token') ?? '';

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (password !== repeat) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    void run(async () => {
      await api.post('/auth/reset-password', { token, password });
      navigate('/login', { replace: true });
    });
  };

  return (
    <AuthFrame>
      <form onSubmit={submit} noValidate>
        <div>
          <h1>Новый пароль</h1>
          <p className="muted" style={{ marginTop: 6 }}>
            Не короче {PASSWORD_MIN_LENGTH} символов. После смены все старые входы закроются.
          </p>
        </div>
        {!token && <div className="alert">В ссылке нет токена. Откройте ссылку из письма целиком.</div>}
        {error && <div className="alert">{error.fields.password ?? error.message}</div>}
        <label className="field">
          <span>Пароль</span>
          <input className="input" type="password" autoComplete="new-password" minLength={PASSWORD_MIN_LENGTH} required value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
        <label className="field">
          <span>Повторите пароль</span>
          <input className="input" type="password" autoComplete="new-password" required value={repeat} onChange={(e) => setRepeat(e.target.value)} aria-invalid={mismatch} />
          {mismatch && <span className="field-error">Пароли не совпадают</span>}
        </label>
        <button className="btn btn-primary btn-block" disabled={busy || !token}>
          Сохранить пароль
        </button>
      </form>
    </AuthFrame>
  );
}
